import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Button, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { PasswordField } from '../components/PasswordField';
import { newPasswordRequiredSchema, type NewPasswordRequiredFormValues } from '../schemas';

interface NewPasswordRequiredStepProps {
  onSubmit: (newPassword: string) => Promise<void>;
}

export function NewPasswordRequiredStep({
  onSubmit,
}: NewPasswordRequiredStepProps): JSX.Element {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<NewPasswordRequiredFormValues>({
    resolver: zodResolver(newPasswordRequiredSchema),
    defaultValues: { newPassword: '', confirmPassword: '' },
  });

  async function handleFormSubmit(values: NewPasswordRequiredFormValues): Promise<void> {
    setSubmitError(null);
    try {
      await onSubmit(values.newPassword);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Error al cambiar la contraseña');
    }
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          Es tu primer ingreso. Por seguridad, definí una nueva contraseña.
        </Typography>

        {submitError && <Alert severity="error">{submitError}</Alert>}

        <PasswordField
          label="Nueva contraseña"
          autoFocus
          fullWidth
          autoComplete="new-password"
          {...register('newPassword')}
          error={!!errors.newPassword}
          helperText={errors.newPassword?.message}
        />

        <PasswordField
          label="Confirmar nueva contraseña"
          fullWidth
          autoComplete="new-password"
          {...register('confirmPassword')}
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword?.message}
        />

        <Button type="submit" variant="contained" size="large" disabled={isSubmitting} fullWidth>
          {isSubmitting ? 'Cambiando...' : 'Cambiar contraseña'}
        </Button>
      </Stack>
    </form>
  );
}
